import { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Gauge,
  Radar,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { AiOutput, Badge, Button, Card, SectionHeader, Spinner } from "../components/Ui";
import {
  macroAssetViews,
  macroCalendar,
  macroGroupNotes,
  macroPromptPresets,
  macroScenarios,
  macroSignals,
  type MacroGroup,
  type MacroHorizon,
  type MacroSignal,
  type MacroTone,
} from "../data/macro";
import { api } from "../lib/api";
import { useData } from "../lib/data";

const macroGroups = Object.keys(macroGroupNotes) as MacroGroup[];

function Sparkline({ values, tone }: { values: number[]; tone: MacroTone }) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const points = values.map((value, index) => {
    const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100;
    const y = max === min ? 15 : 28 - ((value - min) / (max - min)) * 22;
    return `${x},${y}`;
  }).join(" ");
  return <svg className={`macro-spark macro-spark-${tone}`} viewBox="0 0 100 32" role="img" aria-label="指标近五期走势"><polyline points={points} /><circle cx="100" cy={points.split(" ").at(-1)?.split(",")[1]} r="2.4" /></svg>;
}

function SignalTone({ signal }: { signal: MacroSignal }) {
  return <span className={`macro-signal-tone tone-${signal.tone}`}>{signal.assessment}</span>;
}

function signalBalance(signals: MacroSignal[]) {
  if (!signals.length) return 50;
  const raw = signals.reduce((sum, item) => sum + (item.tone === "up" ? 1 : item.tone === "down" ? -1 : 0), 0) / signals.length;
  return Math.round(50 + raw * 35);
}

function MacroRadar({ groups }: { groups: Array<{ group: MacroGroup; score: number }> }) {
  const center = { x: 160, y: 118 };
  const maxRadius = 82;
  const point = (index: number, ratio: number) => {
    const angle = -Math.PI / 2 + index * Math.PI * 2 / groups.length;
    return { x: center.x + Math.cos(angle) * maxRadius * ratio, y: center.y + Math.sin(angle) * maxRadius * ratio };
  };
  const polygon = groups.map((item, index) => {
    const p = point(index, item.score / 100);
    return `${p.x},${p.y}`;
  }).join(" ");
  return <div className="macro-radar-wrap">
    <svg viewBox="0 0 320 245" role="img" aria-label="八维宏观信号平衡图">
      {[0.33, 0.66, 1].map((ratio) => <polygon key={ratio} className="radar-grid" points={groups.map((_, index) => { const p = point(index, ratio); return `${p.x},${p.y}`; }).join(" ")} />)}
      {groups.map((_, index) => { const p = point(index, 1); return <line key={index} className="radar-axis" x1={center.x} y1={center.y} x2={p.x} y2={p.y} />; })}
      <polygon className="radar-area" points={polygon} />
      {groups.map((item, index) => { const p = point(index, item.score / 100); const label = point(index, 1.25); return <g key={item.group}><circle className="radar-dot" cx={p.x} cy={p.y} r="3" /><text x={label.x} y={label.y} textAnchor="middle">{item.group}</text><text className="radar-score" x={label.x} y={label.y + 11} textAnchor="middle">{item.score}</text></g>; })}
    </svg>
    <small>信号平衡指数仅用于比较“改善 / 承压”数量，不是官方经济指数，也不代表资产涨跌预测。</small>
  </div>;
}

export function MacroPage() {
  const { data, refresh } = useData();
  const [horizon, setHorizon] = useState<MacroHorizon>("3M");
  const [scenarioId, setScenarioId] = useState("base");
  const [activeGroup, setActiveGroup] = useState<MacroGroup | "全部">("全部");
  const [prompt, setPrompt] = useState(macroPromptPresets[0]);
  const [answer, setAnswer] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const selectedScenario = useMemo(() => macroScenarios.find((item) => item.id === scenarioId) ?? macroScenarios[0], [scenarioId]);
  const filteredSignals = activeGroup === "全部" ? macroSignals : macroSignals.filter((item) => item.group === activeGroup);
  const groupSummary = useMemo(() => macroGroups.map((group) => {
    const signals = macroSignals.filter((item) => item.group === group);
    return { group, signals, score: signalBalance(signals), up: signals.filter((item) => item.tone === "up").length, down: signals.filter((item) => item.tone === "down").length };
  }), []);
  const positiveSignals = macroSignals.filter((item) => item.tone === "up").length;
  const negativeSignals = macroSignals.filter((item) => item.tone === "down").length;
  const neutralSignals = macroSignals.length - positiveSignals - negativeSignals;

  const run = async () => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const result = await api.research("macro", prompt, {
        asOf: "2026-07-19",
        institution: data?.profile,
        template: data?.templates.find((item) => item.workflow === "macro" && item.isDefault),
        governedSources: data?.sources.filter((item) => item.status === "connected"),
        horizon,
        selectedScenario,
        signals: macroSignals.map(({ group, name, value, previous, change, assessment, signal, implication, publishedAt, frequency, confidence, source, url }) => ({ group, name, value, previous, change, assessment, signal, implication, publishedAt, frequency, confidence, source, url })),
        assetViews: macroAssetViews[horizon],
        objectivityRule: "必须同时列出支持证据、反向证据、数据缺口和证伪条件；不得使用乐观或悲观修辞替代事实。",
      });
      setAnswer(result.content);
      setModel(result.model);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "宏观研究生成失败");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!answer) return;
    await api.saveMemory({
      title: `宏观复核 · ${selectedScenario.title}`,
      thesis: prompt,
      evidence: `截至2026-07-19；${macroSignals.length}项宏观与社会证据；${horizon}视角；${selectedScenario.label}${selectedScenario.probability}%：${answer.slice(0, 420)}`,
      status: "待验证",
      confidence: 66,
      decisionType: "宏观判断",
      asset: data?.profile.portfolioName || "多资产组合",
      tags: [horizon, selectedScenario.label, "宏观"],
      reviewDate: "2026-07-31",
      source: "宏观决策台",
    });
    await refresh();
    setSaved(true);
  };

  return <div className="page-stack macro-page macro-page-v3">
    <div className="page-title">
      <div><span className="eyebrow">MACRO & SOCIETY MONITOR</span><h1>宏观环境</h1><p>从生产、需求、就业民生到信用与资产负债表，客观描述环境，不用单一指标讲故事。</p></div>
      <Badge tone="neutral"><Clock3 size={12} />证据截面 · 2026-07-19</Badge>
    </div>

    <Card className="macro-brief-card">
      <div className="macro-brief-main">
        <span className="macro-kicker">BALANCED VIEW</span>
        <h2>生产与外贸有韧性，内需、信用和地产仍构成约束</h2>
        <p>当前更接近“弱修复与结构分化”，而不是全面复苏或全面衰退。就业总量稳定，但消费支出、财产性收入和住户信用显示居民体感仍偏谨慎；因此保持组合基准，等待多维数据共同确认。</p>
        <div className="macro-brief-flags"><span><CheckCircle2 size={14} />事实与判断分层</span><span><Users size={14} />纳入社会民生</span><span><ShieldAlert size={14} />保留反向证据</span></div>
      </div>
      <div className="macro-brief-metrics">
        <div><span>覆盖指标</span><strong>{macroSignals.length} 项</strong><small>8 个经济与社会维度</small></div>
        <div><span>信号分布</span><strong>{positiveSignals} / {neutralSignals} / {negativeSignals}</strong><small>改善 / 中性分化 / 承压</small></div>
        <div><span>基准情景</span><strong>{macroScenarios[0].probability}%</strong><small>概率用于组织讨论</small></div>
        <div><span>下次强制复核</span><strong>07-31</strong><small>7月 PMI 发布窗口</small></div>
      </div>
    </Card>

    <Card className="macro-panorama-card">
      <SectionHeader title="八维宏观全景" subtitle="同一张图同时呈现经济总量、社会体感和资产负债表；点击维度筛选下方证据。" action={<Radar size={17} />} />
      <div className="macro-panorama-layout">
        <MacroRadar groups={groupSummary.map(({ group, score }) => ({ group, score }))} />
        <div className="macro-dimension-grid">
          {groupSummary.map((item) => <button key={item.group} className={activeGroup === item.group ? "active" : ""} onClick={() => setActiveGroup(activeGroup === item.group ? "全部" : item.group)}>
            <span><b>{item.group}</b><em>{item.signals.length}项</em></span>
            <strong>{item.score}</strong>
            <i><u style={{ width: `${item.score}%` }} /></i>
            <small>{macroGroupNotes[item.group].conclusion}</small>
            <footer><span className="tone-up">改善 {item.up}</span><span className="tone-down">承压 {item.down}</span></footer>
          </button>)}
        </div>
      </div>
    </Card>

    <div className="macro-state-grid">
      <Card className="macro-regime-card">
        <SectionHeader title="宏观状态定位" subtitle="增长动能 × 价格压力；阴影表示判断区间而非精确坐标。" action={<Gauge size={17} />} />
        <div className="macro-regime-map" aria-label="宏观状态矩阵">
          <div className="macro-quadrant q-recovery"><strong>复苏</strong><span>增长↑ / 通胀↓</span></div><div className="macro-quadrant q-overheat"><strong>过热</strong><span>增长↑ / 通胀↑</span></div><div className="macro-quadrant q-recession"><strong>衰退</strong><span>增长↓ / 通胀↓</span></div><div className="macro-quadrant q-stagflation"><strong>滞胀</strong><span>增长↓ / 通胀↑</span></div>
          <div className="macro-regime-zone" /><div className="macro-regime-point"><i /><b>当前</b><small>弱修复 / 价格分化</small></div><span className="macro-axis axis-growth">增长动能 →</span><span className="macro-axis axis-inflation">价格压力 →</span>
        </div>
        <div className="macro-regime-foot"><AlertTriangle size={14} /><span>GDP、工业、消费和地产并不同向，状态区间必须保持较宽，不能用单一象限替代判断。</span></div>
      </Card>
      <Card className="macro-change-card">
        <SectionHeader title="本期最重要的三组分歧" subtitle="分歧本身就是风险信息，不强行合成单一方向。" action={<RefreshCw size={16} />} />
        <div className="macro-change-list">
          <article className="warning"><span>01</span><div><strong>生产强，需求弱</strong><p>工业增加值加快，但零售、实际消费支出和产能利用率显示需求承接不足。</p></div><b>≠</b></article>
          <article className="warning"><span>02</span><div><strong>流动性足，信用需求慢</strong><p>M2 保持增长，M1、社融和住户贷款却偏弱，宽货币尚未全面转为活跃需求。</p></div><b>≠</b></article>
          <article className="warning"><span>03</span><div><strong>就业稳，财富效应弱</strong><p>失业率回落，但财产净收入仅增长1.1%，居民消费倾向仍需修复。</p></div><b>≠</b></article>
        </div>
        <div className="macro-no-action"><Activity size={15} /><div><strong>客观结论：暂不由宏观页触发大幅调仓</strong><span>至少等待消费、信用、盈利三类指标中两类同向确认；反向变化同样触发复核。</span></div></div>
      </Card>
    </div>

    <Card className="macro-evidence-card">
      <SectionHeader title="宏观与社会证据账本" subtitle="最新值、比较基准、频率、可信度、投资传导和原始来源同时展示。" action={<span className="macro-source-count">{filteredSignals.length} / {macroSignals.length} 项</span>} />
      <div className="macro-filter-tabs"><button className={activeGroup === "全部" ? "active" : ""} onClick={() => setActiveGroup("全部")}>全部</button>{macroGroups.map((group) => <button key={group} className={activeGroup === group ? "active" : ""} onClick={() => setActiveGroup(group)}>{group}</button>)}</div>
      <div className="macro-evidence-head"><span>维度 / 指标</span><span>最新 / 比较</span><span>近五期</span><span>客观状态</span><span>传导与限制</span><span>口径 / 来源</span></div>
      <div className="macro-evidence-list">{filteredSignals.map((signal) => <article key={`${signal.group}-${signal.name}`}>
        <div><small>{signal.group}</small><strong>{signal.name}</strong><span>{signal.change}</span></div>
        <div className="macro-values"><strong>{signal.value}</strong><span>比较 {signal.previous}</span></div>
        <Sparkline values={signal.series} tone={signal.tone} />
        <div><SignalTone signal={signal} /><small>{signal.signal}</small></div>
        <p>{signal.implication}</p>
        <a href={signal.url} target="_blank" rel="noreferrer"><strong>{signal.source}</strong><span>{signal.publishedAt} · {signal.frequency} · {signal.confidence}可信</span><ExternalLink size={12} /></a>
      </article>)}</div>
    </Card>

    <Card className="macro-asset-card">
      <SectionHeader title="资产与组合含义" subtitle="仅给倾向与风险护栏；同一环境在不同持有期下可以有不同表达。" action={<div className="macro-horizon-tabs">{(["1M", "3M", "6M"] as MacroHorizon[]).map((item) => <button className={horizon === item ? "active" : ""} key={item} onClick={() => setHorizon(item)}>{item}</button>)}</div>} />
      <div className="macro-asset-head"><span>资产</span><span>倾向</span><span>信号刻度</span><span>建议表达</span><span>风险护栏</span></div>
      <div className="macro-asset-list">{macroAssetViews[horizon].map((item) => <article key={item.asset}><strong>{item.asset}</strong><Badge tone={item.tone}>{item.stance}</Badge><div className="macro-score"><span /><i style={{ left: `${item.score}%` }} /><small>防御</small><b>{item.score}</b><small>进取</small></div><p>{item.expression}</p><span className="macro-guardrail">{item.guardrail}</span></article>)}</div>
      <div className="macro-asset-note"><ShieldAlert size={15} /><span>宏观倾向不能直接变成交易。任何权重变化仍需进入组合校准，通过政策区间、成本、流动性和授权检查。</span><a href="/allocation">打开组合校准 <ArrowRight size={13} /></a></div>
    </Card>

    <div className="macro-scenario-layout">
      <Card className="macro-scenario-card">
        <SectionHeader title="客观风险情景" subtitle="概率是可修订的工作假设；每个情景同时写进入条件与证伪条件。" action={<Radar size={17} />} />
        <div className="macro-scenario-tabs">{macroScenarios.map((scenario) => <button key={scenario.id} className={scenarioId === scenario.id ? "active" : ""} onClick={() => setScenarioId(scenario.id)}><span>{scenario.label}</span><strong>{scenario.probability}%</strong><i style={{ width: `${scenario.probability}%` }} /></button>)}</div>
        <div className="macro-scenario-detail"><span className={`macro-scenario-label tone-${selectedScenario.tone}`}>{selectedScenario.label.toUpperCase()}</span><h3>{selectedScenario.title}</h3><p>{selectedScenario.narrative}</p><div className="macro-trigger-grid"><div><strong><CheckCircle2 size={14} />进入条件</strong><ul>{selectedScenario.triggers.map((item) => <li key={item}>{item}</li>)}</ul></div><div><strong><ShieldAlert size={14} />证伪条件</strong><ul>{selectedScenario.invalidations.map((item) => <li key={item}>{item}</li>)}</ul></div></div><div className="macro-scenario-impact"><Sparkles size={15} /><div><strong>资产映射</strong><span>{selectedScenario.assetImpact}</span></div></div></div>
      </Card>
      <Card className="macro-calendar-card"><SectionHeader title="未来复核日历" subtitle="提前写清下一组数据要回答的问题。" action={<CalendarDays size={17} />} /><div className="macro-calendar-list">{macroCalendar.map((item) => <article key={`${item.date}-${item.event}`}><time>{item.date}</time><div><strong>{item.event}</strong><p>{item.question}</p><span>{item.action}</span></div><Badge tone={item.importance === "高" ? "down" : "neutral"}>{item.importance}</Badge></article>)}</div></Card>
    </div>

    <Card className="macro-research-card">
      <SectionHeader title="宏观反证研究" subtitle="AI 必须列事实、反向证据、数据缺口和证伪条件，不能用情绪性措辞替代分析。" action={<BrainCircuit size={18} />} />
      <div className="macro-research-grid"><div><label className="macro-prompt-field"><span>研究问题</span><textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={4} maxLength={240} /><small>{prompt.length}/240 · 当前视角 {horizon} · {selectedScenario.label}</small></label><div className="macro-prompt-presets">{macroPromptPresets.map((item) => <button key={item} onClick={() => setPrompt(item)}>{item}</button>)}</div><Button className="full" onClick={run} disabled={busy || prompt.trim().length < 6}>{busy ? <Spinner label="正在核对证据" /> : <><BrainCircuit size={16} />生成可反驳的宏观判断</>}</Button>{error && <div className="form-error">{error}</div>}</div>
        <div className="macro-research-protocol"><span>OBJECTIVITY PROTOCOL</span><ol><li><b>01</b><div><strong>事实先行</strong><small>引用数值、日期、频率和来源。</small></div></li><li><b>02</b><div><strong>多维交叉</strong><small>生产、需求、就业、信用不能相互替代。</small></div></li><li><b>03</b><div><strong>对称反证</strong><small>上行与下行证据使用同一标准。</small></div></li><li><b>04</b><div><strong>承认未知</strong><small>数据缺口不使用模型猜测补齐。</small></div></li></ol></div>
      </div>
      {answer && <><AiOutput content={answer} model={model} /><div className="macro-save-row"><div><strong>保留为可复盘判断</strong><span>写入投资记忆后，在下一次数据发布时检查是否兑现。</span></div><Button onClick={save} disabled={saved}>{saved ? "已写入投资记忆" : "确认并写入投资记忆"}</Button></div></>}
    </Card>
  </div>;
}
